import type { ButtonHTMLAttributes } from 'react';
import { kaiFontFamily, styleguideColorsExtended as colors } from '../../../core/tokens';
import { resolveStyleOverride, type StyleOverride } from '../../styleOverrides';
import { kaiTypographyWeb } from '../../typography';
import { UploadIcon } from '../Icons';

export type GalleryUploadDropzoneState = 'idle' | 'dragging' | 'disabled';

export interface GalleryUploadDropzoneProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  title: string;
  helperText?: string;
  state?: GalleryUploadDropzoneState;
  onActivate?: () => void;
  styleOverride?: StyleOverride;
}

export const GalleryUploadDropzone = ({
  title,
  helperText,
  state = 'idle',
  onActivate,
  styleOverride: dropzoneCss,
  style,
  disabled,
  onClick,
  ...props
}: GalleryUploadDropzoneProps) => {
  const unavailable = disabled || state === 'disabled';
  const dragging = state === 'dragging';

  return (
    <button
      type='button'
      aria-label={title}
      disabled={unavailable}
      data-gallery-upload-dropzone='true'
      data-gallery-upload-state={unavailable ? 'disabled' : state}
      onClick={event => {
        onClick?.(event);
        if (!event.defaultPrevented && !unavailable) onActivate?.();
      }}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        minHeight: 160,
        padding: '30px 20px',
        borderWidth: 1.5,
        borderStyle: dragging ? 'solid' : 'dashed',
        borderColor: dragging ? colors.ink : unavailable ? colors.stroke : colors.midtone,
        background: dragging ? colors.wash : colors.sunken,
        color: unavailable ? colors.midtoneFaded : colors.ink,
        cursor: unavailable ? 'not-allowed' : 'pointer',
        fontFamily: kaiFontFamily,
        opacity: unavailable ? 0.72 : 1,
        textAlign: 'center',
        transition: 'background 140ms ease, border-color 140ms ease, opacity 140ms ease',
        ...style,
        ...resolveStyleOverride(dropzoneCss),
      }}
      {...props}
    >
      <span
        aria-hidden='true'
        style={{
          width: 34,
          height: 34,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 12,
          border: `1.5px solid ${dragging ? colors.ink : unavailable ? colors.stroke : colors.midtone}`,
          color: dragging ? colors.ink : unavailable ? colors.midtoneFaded : colors.midtone,
        }}
      >
        <UploadIcon size={16} color='currentColor' />
      </span>
      <span style={{ ...kaiTypographyWeb.xSmallMed, color: unavailable ? colors.midtoneFaded : colors.ink, marginBottom: helperText ? 4 : 0 }}>
        {title}
      </span>
      {helperText
        ? (
          <span style={{ ...kaiTypographyWeb.xxSmall, color: unavailable ? colors.midtoneFaded : colors.textTertiary, letterSpacing: '0.02em' }}>
            {helperText}
          </span>
        )
        : null}
    </button>
  );
};
